import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Modal,
  Spin,
  Table,
  Select,
  Input,
  Dropdown,
  Tooltip,
  Pagination,
} from "antd";
import {
  getAllPlans,
  getPlanStats,
  deletePlanById,
  duplicatePlan,
  updatePlanStatus,
  togglePlanPopular,
} from "@api/subscription/plan";
import { SuccessNotification, ErrorNotification } from "@components";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiMoreVertical,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiLayers,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiStar,
  FiEye,
  FiEyeOff,
  FiUsers,
  FiZap,
  FiCreditCard,
  FiBox,
} from "react-icons/fi";
import "@assets/css/PlanList.css";

const { Option } = Select;

const PlansList = ({ history }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    deprecated: 0,
    comingSoon: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const [filters, setFilters] = useState({
    status: "",
    visibility: "",
    planType: "",
    search: "",
  });

  useEffect(() => {
    fetchPlans();
    fetchStats();
  }, [pagination.page, filters.status, filters.visibility, filters.planType]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status || undefined,
        visibility: filters.visibility || undefined,
        planType: filters.planType || undefined,
      };

      const { data } = await getAllPlans(params);
      setPlans(data.plans || []);
      if (data.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: data.pagination.total,
          pages: data.pagination.pages,
        }));
      }
    } catch (error) {
      console.error("Error fetching plans", error);
      // Fallback for old API format
      if (Array.isArray(error?.response?.data) || Array.isArray(error)) {
        setPlans(error?.response?.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await getPlanStats();
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats", error);
    }
  };

  const handlePlanCreate = () => {
    history.push("/super-admin/plans/create");
  };

  const handlePlanUpdate = (id) => {
    history.push(`/super-admin/plans/update/${id}`);
  };

  const handlePlanDelete = (id, name) => {
    Modal.confirm({
      title: "Delete Plan",
      content: (
        <div>
          <p>Are you sure you want to delete <strong>{name}</strong>?</p>
          <p style={{ color: "#ef4444", fontSize: 13 }}>
            This action cannot be undone. Users on this plan may lose access.
          </p>
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          await deletePlanById(id);
          fetchPlans();
          fetchStats();
          SuccessNotification("Plan deleted successfully");
        } catch (error) {
          ErrorNotification("Error deleting plan");
        }
      },
    });
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicatePlan(id);
      fetchPlans();
      fetchStats();
      SuccessNotification("Plan duplicated successfully");
    } catch (error) {
      ErrorNotification("Error duplicating plan");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updatePlanStatus(id, status);
      fetchPlans();
      fetchStats();
      SuccessNotification("Plan status updated");
    } catch (error) {
      ErrorNotification("Error updating status");
    }
  };

  const handleTogglePopular = async (id) => {
    try {
      await togglePlanPopular(id);
      fetchPlans();
      SuccessNotification("Popular status updated");
    } catch (error) {
      ErrorNotification("Error updating popular status");
    }
  };

  const handleFilterReset = () => {
    setFilters({ status: "", visibility: "", planType: "", search: "" });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getDropdownMenu = (record) => ({
    items: [
      {
        key: "edit",
        label: "Edit Plan",
        icon: <FiEdit2 />,
        onClick: () => handlePlanUpdate(record._id),
      },
      {
        key: "duplicate",
        label: "Duplicate",
        icon: <FiCopy />,
        onClick: () => handleDuplicate(record._id),
      },
      {
        key: "popular",
        label: record.isPopular ? "Remove Popular" : "Mark Popular",
        icon: <FiStar />,
        onClick: () => handleTogglePopular(record._id),
      },
      { type: "divider" },
      {
        key: "activate",
        label: "Set Active",
        icon: <FiCheckCircle />,
        onClick: () => handleStatusChange(record._id, "active"),
        disabled: record.status === "active",
      },
      {
        key: "deactivate",
        label: "Set Inactive",
        icon: <FiXCircle />,
        onClick: () => handleStatusChange(record._id, "inactive"),
        disabled: record.status === "inactive",
      },
      { type: "divider" },
      {
        key: "delete",
        label: "Delete",
        icon: <FiTrash2 />,
        danger: true,
        onClick: () => handlePlanDelete(record._id, record.name),
      },
    ],
  });

  const getPlanTypeIcon = (type) => {
    const firstLetter = type?.charAt(0)?.toUpperCase() || "S";
    return firstLetter;
  };

  const filteredPlans = plans.filter((plan) => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        plan.name?.toLowerCase().includes(searchLower) ||
        plan.metaDescription?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const columns = [
    {
      title: "Plan",
      dataIndex: "name",
      key: "name",
      width: 280,
      render: (_, record) => (
        <div className="plan-name-cell">
          <div className={`plan-icon-wrapper ${record.planType || "starter"}`}>
            {getPlanTypeIcon(record.planType)}
          </div>
          <div className="plan-name-info">
            <div className="plan-name-text">
              {record.name}
              {record.isPopular && (
                <span className="plan-popular-badge">Popular</span>
              )}
            </div>
            <div className="plan-description">
              {record.metaDescription || "No description"}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "planType",
      key: "planType",
      width: 120,
      render: (type) => (
        <span className={`plan-type-tag ${type || "starter"}`}>
          {type || "Starter"}
        </span>
      ),
    },
    {
      title: "Pricing",
      key: "pricing",
      width: 150,
      render: (_, record) => {
        const monthly = record.pricing?.monthly ?? record.monthlyPrice ?? 0;
        const annual = record.pricing?.annual ?? record.annualPrice ?? 0;
        const currency = record.pricing?.currency || "USD";
        const currencySymbol = currency === "USD" ? "$" : currency === "EUR" ? "€" : currency === "GBP" ? "£" : "₹";
        // Convert from cents to dollars for display
        const monthlyDisplay = monthly / 100;
        const annualDisplay = annual / 100;

        return (
          <div className="plan-pricing-cell">
            <div className="plan-price-main">
              <span className="plan-price-currency">{currencySymbol}</span>
              <span className="plan-price-amount">{monthlyDisplay.toFixed(2)}</span>
              <span className="plan-price-period">/mo</span>
            </div>
            {annualDisplay > 0 && (
              <div className="plan-price-annual">
                {currencySymbol}{(annualDisplay / 12).toFixed(2)}/mo annually
                {record.annualSavings > 0 && (
                  <span className="savings"> (Save {record.annualSavings}%)</span>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Limits",
      key: "limits",
      width: 220,
      render: (_, record) => {
        const limits = record.globalLimits || {};
        const copilots = limits.maxCopilots ?? record.maxCopilots ?? 1;
        const credits = limits.maxCreditsPerMonth ?? record.maxCreditsPerMonth ?? 1000;
        const team = limits.maxTeamMembers ?? 1;

        return (
          <div className="plan-limits-cell">
            <Tooltip title="Copilots">
              <span className="plan-limit-badge">
                <FiBox /> {copilots}
              </span>
            </Tooltip>
            <Tooltip title="Credits/mo">
              <span className="plan-limit-badge">
                <FiCreditCard /> {credits >= 100000 ? "∞" : credits.toLocaleString()}
              </span>
            </Tooltip>
            <Tooltip title="Team members">
              <span className="plan-limit-badge">
                <FiUsers /> {team}
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Stripe",
      key: "stripe",
      width: 100,
      render: (_, record) => {
        const hasStripe = record.stripe?.productId || record.stripeProductId;
        return (
          <span className={`stripe-status-badge ${hasStripe ? "connected" : "not-connected"}`}>
            {hasStripe ? <FiCheckCircle /> : <FiXCircle />}
            {hasStripe ? "Connected" : "Not Set"}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <span className={`plan-status-badge ${status || "active"}`}>
          <span className="plan-status-dot" />
          {status === "coming_soon" ? "Coming Soon" : (status || "Active").charAt(0).toUpperCase() + (status || "active").slice(1)}
        </span>
      ),
    },
    {
      title: "Visibility",
      dataIndex: "visibility",
      key: "visibility",
      width: 100,
      render: (visibility) => (
        <span className={`plan-visibility-badge ${visibility || "public"}`}>
          {visibility === "public" ? <FiEye /> : <FiEyeOff />}
          {(visibility || "Public").charAt(0).toUpperCase() + (visibility || "public").slice(1)}
        </span>
      ),
    },
    {
      title: "Tier",
      dataIndex: "tier",
      key: "tier",
      width: 70,
      align: "center",
      render: (tier) => (
        <span className={`plan-tier-badge tier-${tier ?? 1}`}>
          {tier ?? 1}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <div className="plan-actions">
          <Tooltip title="Edit">
            <button
              className="plan-action-btn edit"
              onClick={() => handlePlanUpdate(record._id)}
            >
              <FiEdit2 />
            </button>
          </Tooltip>
          <Tooltip title="Duplicate">
            <button
              className="plan-action-btn duplicate"
              onClick={() => handleDuplicate(record._id)}
            >
              <FiCopy />
            </button>
          </Tooltip>
          <Dropdown
            menu={getDropdownMenu(record)}
            trigger={["click"]}
            placement="bottomRight"
          >
            <button className="plan-action-btn">
              <FiMoreVertical />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div className="plan-list-container">
      {/* Header */}
      <div className="plan-list-header">
        <div className="plan-list-title-section">
          <h1 className="plan-list-title">Subscription Plans</h1>
          <p className="plan-list-subtitle">
            Manage pricing plans, features, and credit allocations
          </p>
        </div>
        <div className="plan-list-actions">
          <Button
            icon={<FiRefreshCw />}
            onClick={() => { fetchPlans(); fetchStats(); }}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<FiPlus />}
            onClick={handlePlanCreate}
          >
            Create Plan
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="plan-stats-grid">
        <div className="plan-stat-card total">
          <div className="plan-stat-header">
            <div className="plan-stat-icon">
              <FiLayers />
            </div>
          </div>
          <div className="plan-stat-value">{stats.total || 0}</div>
          <div className="plan-stat-label">Total Plans</div>
        </div>
        <div className="plan-stat-card active">
          <div className="plan-stat-header">
            <div className="plan-stat-icon">
              <FiCheckCircle />
            </div>
          </div>
          <div className="plan-stat-value">{stats.active || 0}</div>
          <div className="plan-stat-label">Active</div>
        </div>
        <div className="plan-stat-card inactive">
          <div className="plan-stat-header">
            <div className="plan-stat-icon">
              <FiXCircle />
            </div>
          </div>
          <div className="plan-stat-value">{stats.inactive || 0}</div>
          <div className="plan-stat-label">Inactive</div>
        </div>
        <div className="plan-stat-card deprecated">
          <div className="plan-stat-header">
            <div className="plan-stat-icon">
              <FiClock />
            </div>
          </div>
          <div className="plan-stat-value">{stats.deprecated || 0}</div>
          <div className="plan-stat-label">Deprecated</div>
        </div>
        <div className="plan-stat-card coming">
          <div className="plan-stat-header">
            <div className="plan-stat-icon">
              <FiZap />
            </div>
          </div>
          <div className="plan-stat-value">{stats.comingSoon || 0}</div>
          <div className="plan-stat-label">Coming Soon</div>
        </div>
      </div>

      {/* Filters */}
      <div className="plan-filters-section">
        <div className="plan-filter-group">
          <FiSearch style={{ color: "#94a3b8" }} />
          <Input
            placeholder="Search plans..."
            className="plan-search-input"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            allowClear
          />
        </div>
        <div className="plan-filter-group">
          <span className="plan-filter-label">Status</span>
          <Select
            placeholder="All Status"
            value={filters.status || undefined}
            onChange={(value) => {
              setFilters({ ...filters, status: value || "" });
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            allowClear
            style={{ width: 140 }}
          >
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
            <Option value="deprecated">Deprecated</Option>
            <Option value="coming_soon">Coming Soon</Option>
          </Select>
        </div>
        <div className="plan-filter-group">
          <span className="plan-filter-label">Visibility</span>
          <Select
            placeholder="All"
            value={filters.visibility || undefined}
            onChange={(value) => {
              setFilters({ ...filters, visibility: value || "" });
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            allowClear
            style={{ width: 120 }}
          >
            <Option value="public">Public</Option>
            <Option value="private">Private</Option>
            <Option value="hidden">Hidden</Option>
          </Select>
        </div>
        <div className="plan-filter-group">
          <span className="plan-filter-label">Type</span>
          <Select
            placeholder="All Types"
            value={filters.planType || undefined}
            onChange={(value) => {
              setFilters({ ...filters, planType: value || "" });
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            allowClear
            style={{ width: 150 }}
          >
            <Option value="free">Free</Option>
            <Option value="starter">Starter</Option>
            <Option value="professional">Professional</Option>
            <Option value="business">Business</Option>
            <Option value="enterprise">Enterprise</Option>
            <Option value="custom">Custom</Option>
          </Select>
        </div>
        {(filters.status || filters.visibility || filters.planType || filters.search) && (
          <Button
            className="plan-filter-reset"
            onClick={handleFilterReset}
            icon={<FiFilter />}
          >
            Reset Filters
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="plan-table-card">
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredPlans}
            rowKey={(record) => record._id}
            pagination={false}
            scroll={{ x: 1400 }}
            locale={{
              emptyText: (
                <div className="plan-empty-state">
                  <div className="plan-empty-icon">
                    <FiLayers />
                  </div>
                  <div className="plan-empty-title">No plans found</div>
                  <div className="plan-empty-description">
                    {filters.status || filters.visibility || filters.planType || filters.search
                      ? "Try adjusting your filters to find what you're looking for."
                      : "Get started by creating your first subscription plan."}
                  </div>
                  {!filters.status && !filters.visibility && !filters.planType && !filters.search && (
                    <Button type="primary" icon={<FiPlus />} onClick={handlePlanCreate}>
                      Create Plan
                    </Button>
                  )}
                </div>
              ),
            }}
          />
        </Spin>
        {pagination.total > pagination.limit && (
          <div className="plan-pagination">
            <div className="plan-pagination-info">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
              {pagination.total} plans
            </div>
            <Pagination
              current={pagination.page}
              total={pagination.total}
              pageSize={pagination.limit}
              onChange={(page) => setPagination((prev) => ({ ...prev, page }))}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlansList;
